// FattApp.jsx — shell interna del modulo Fatturazione (fatt-v1, CONTRATTO-MVP §UI).
// Routing tra le viste (Nuova/Emesse/Clienti/Voci/Impostazioni + Editor),
// header con le tab interne (= nav della register), toast locale f1-toast,
// stile CSS iniettato UNA volta (STYLE_ID) sui token --shell-* con fallback.
// Le liste usano il componente condiviso client/src/ui/DataTable.jsx (stati
// vuoto/caricamento/errore+Riprova cablati — regole UI 4).
//
// La factory createFattV1 (in fondo) è l'entry del registry: mount IDEMPOTENTE
// su #fatt-root (creato se assente, pattern sop-v3), pendingTab se il
// componente non è ancora montato, navigate/openDeepLink con toast dal
// catalogo per le voci ignote (mai silenzio).

import React from 'react';
import ReactDOM from 'react-dom';
import { label } from '../shell/glossary.js';
import { DataTable } from '../ui/DataTable.jsx';
import { api, TAB_IDS } from './api.js';
import { Editor, ClienteForm } from './Editor.jsx';
import {
  NATURE, ALIQUOTE_IVA, STATO_LABEL,
  fmtEuro, fmtDay, nomeCliente, numOrNull, debounce,
} from './editor-core.js';

// ---------------------------------------------------------------------------
// Stile — classi prefissate f1-, chrome sui token --shell-* con fallback,
// dark-mode friendly (i token cambiano, i fallback sono il tema chiaro).
// Badge stato: SOLO 'bozza' grigio nell'MVP (il piano vieta comunque il rosso
// per non_consegnata).
// ---------------------------------------------------------------------------
const STYLE_ID = 'fatt1-style';

function css() {
  return [
    // il contenuto parte DOPO il rail (84px, non i 56 del rail v1: col valore
    // vecchio il titolo finiva sotto la barra — collaudo utente 2026-07-20) e,
    // quando comanda il sottomenù shell, anche dopo la sua colonna.
    '#fatt-root{position:absolute;inset:0;left:var(--shell-rail-w,84px);overflow:auto;background:var(--shell-bg,#f8fafc);color:var(--shell-text,#0f172a);font:14px/1.45 system-ui,Segoe UI,Roboto,sans-serif;padding:20px 24px;box-sizing:border-box}',
    '@media(min-width:768px){body.shell-submenu #fatt-root{left:calc(var(--shell-rail-w,84px) + var(--sidebar-w,240px))}}',
    '@media(max-width:767px){#fatt-root{left:0;padding:12px}}',
    // tab interne: servono solo quando il sottomenù shell NON c'è (mobile o flag
    // spenta) — altrimenti le stesse voci comparirebbero due volte
    '@media(min-width:768px){body.shell-submenu #fatt-root .f1-tabs{display:none}}',
    '.f1-h1{font-size:22px;font-weight:700;margin:0 0 2px}',
    '.f1-sub{color:#64748b;font-size:13px;margin:0 0 14px}',
    '.f1-tabs{display:flex;gap:8px;margin-bottom:16px;flex-wrap:wrap}',
    '.f1-tab{border:1px solid #e2e8f0;background:var(--shell-surface,#fff);border-radius:999px;padding:7px 16px;cursor:pointer;font-weight:600;color:#475569;font:inherit}',
    '.f1-tab.on{background:var(--shell-primary-strong,#0e7490);color:#fff;border-color:var(--shell-primary-strong,#0e7490)}',
    '.f1-card{background:var(--shell-surface,#fff);border:1px solid #e2e8f0;border-radius:12px;padding:16px;margin-bottom:12px}',
    // Cliente + Documento affiancati (collaudo utente 2026-07-20); sotto i 1100px tornano in colonna
    '.f1-duo{display:grid;grid-template-columns:1fr 1fr;gap:12px;align-items:start;margin-bottom:12px}',
    '.f1-duo>.f1-card{margin-bottom:0;height:100%}',
    '@media(max-width:1100px){.f1-duo{grid-template-columns:1fr}}',
    // colonna IVA delle righe: il preset "0% — N6.3 …" deve restare leggibile
    '.f1-iva-cell{min-width:190px}',
    '.f1-iva-cell select{max-width:230px}',
    '.f1-card-t{font-weight:700;margin-bottom:10px;font-size:15px}',
    '.f1-btn{background:var(--shell-primary-strong,#0e7490);color:#fff;border:0;border-radius:8px;padding:8px 14px;font-weight:600;cursor:pointer;font:inherit}',
    '.f1-btn.ghost{background:var(--shell-surface,#fff);color:var(--shell-primary-strong,#0e7490);border:1px solid var(--shell-primary-strong,#0e7490)}',
    '.f1-btn.gray{background:var(--shell-surface,#fff);color:#475569;border:1px solid #cbd5e1}',
    '.f1-btn:disabled{opacity:.55;cursor:default}',
    '.f1-btn.sm{padding:5px 10px;font-size:12px}',
    '.f1-in{border:1px solid #cbd5e1;border-radius:8px;padding:7px 10px;font:inherit;width:100%;box-sizing:border-box;background:var(--shell-surface,#fff);color:var(--shell-text,#0f172a)}',
    '.f1-in:focus{outline:none;border-color:var(--shell-primary,#06b6d4)}',
    '.f1-in:disabled{background:#f1f5f9;color:#64748b}',
    '.f1-in.num{text-align:right}',
    '.f1-in-warn{border-color:#f59e0b;background:#fffbeb}',
    '.f1-muted{color:#94a3b8;font-size:12px}',
    '.f1-hint{color:#94a3b8;font-size:11px;display:block;margin-top:2px}',
    '.f1-fielderr{color:#b91c1c;font-size:11px;display:block;margin-top:2px;font-weight:600}',
    '.f1-campo{display:flex;flex-direction:column;gap:3px;min-width:0}',
    '.f1-campo.wide,.f1-banner.wide{grid-column:1/-1}',
    '.f1-lbl{font-size:11px;font-weight:700;letter-spacing:.03em;text-transform:uppercase;color:#64748b}',
    '.f1-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:10px 12px}',
    '.f1-check{display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer}',
    '.f1-check input{width:16px;height:16px}',
    // editor: due colonne, riepilogo sticky; una colonna sotto 768px
    '.f1-editor{display:grid;grid-template-columns:minmax(0,1fr) 300px;gap:14px;align-items:start}',
    '@media(max-width:767px){.f1-editor{grid-template-columns:1fr}}',
    '.f1-sticky{position:sticky;top:8px}',
    '@media(max-width:767px){.f1-sticky{position:static}}',
    '.f1-ed-bar{display:flex;align-items:center;gap:12px;flex-wrap:wrap;margin-bottom:12px}',
    '.f1-ed-title{font-weight:700;font-size:16px;display:flex;align-items:center;gap:8px}',
    '.f1-ed-actions{margin-left:auto;display:flex;gap:8px;align-items:center;flex-wrap:wrap}',
    '.f1-ind{font-size:12px;font-weight:600;border-radius:999px;padding:3px 10px}',
    '.f1-ind.saving{background:#e0f2fe;color:#075985}',
    '.f1-ind.ok{background:#d1fae5;color:#065f46}',
    '.f1-ind.err{background:#fee2e2;color:#991b1b}',
    '.f1-ind.idle{background:#f1f5f9;color:#64748b}',
    '.f1-badge-stato{font-size:11px;font-weight:700;border-radius:999px;padding:2px 9px}',
    '.f1-badge-stato.bozza{background:#e2e8f0;color:#475569}',
    '.f1-badge-comp{font-size:10px;font-weight:700;border-radius:999px;padding:2px 8px;background:#d1fae5;color:#065f46;margin-left:8px}',
    '.f1-pill{font-size:10px;font-weight:700;border-radius:999px;padding:2px 8px;background:#fef3c7;color:#92400e}',
    '.f1-pill.ok{background:#d1fae5;color:#065f46}',
    // banner
    '.f1-banner{border-radius:10px;padding:10px 12px;margin-bottom:12px;font-size:13px}',
    '.f1-banner.err{background:#fef2f2;border:1px solid #fecaca;color:#b91c1c}',
    '.f1-banner.warn{background:#fffbeb;border:1px solid #fde68a;color:#92400e}',
    '.f1-banner.pa{background:#eff6ff;border:1px solid #bfdbfe;color:#1e40af;margin-top:10px}',
    '.f1-banner.conflict{background:#fff7ed;border:2px solid #fb923c;color:#9a3412}',
    '.f1-conflict-actions{display:flex;gap:8px;margin-top:8px;flex-wrap:wrap}',
    '.f1-warn-inline{color:#92400e;font-size:11px;font-weight:600}',
    // problemi di validazione
    '.f1-problemi{background:var(--shell-surface,#fff);border:1px solid #e2e8f0;border-radius:12px;padding:10px 12px;margin-bottom:12px}',
    '.f1-problemi-h{display:flex;justify-content:space-between;align-items:center;font-weight:700;font-size:13px;margin-bottom:6px}',
    '.f1-prob{display:block;width:100%;text-align:left;border-radius:8px;border:1px solid transparent;padding:6px 9px;margin-bottom:4px;cursor:pointer;font:inherit;font-size:13px;background:transparent}',
    '.f1-prob.err{background:#fef2f2;border-color:#fecaca;color:#b91c1c}',
    '.f1-prob.warn{background:#fffbeb;border-color:#fde68a;color:#92400e}',
    '.f1-prob-cod{font-weight:700;font-size:11px;margin-right:6px}',
    '.f1-prob-campo{float:right;font-size:11px;opacity:.7;font-family:monospace}',
    // combobox cliente
    '.f1-cli-row{display:flex;gap:8px;align-items:flex-start}',
    '.f1-combo{position:relative;flex:1;min-width:0}',
    '.f1-combo-list{position:absolute;top:100%;left:0;right:0;z-index:40;background:var(--shell-surface,#fff);border:1px solid #cbd5e1;border-radius:8px;box-shadow:0 8px 24px rgba(15,23,42,.12);max-height:260px;overflow:auto;margin-top:2px}',
    '.f1-combo-item{display:flex;justify-content:space-between;gap:10px;width:100%;text-align:left;border:0;background:transparent;padding:8px 10px;cursor:pointer;font:inherit}',
    '.f1-combo-item:hover{background:#f1f5f9}',
    '.f1-cli-info{display:flex;gap:14px;margin-top:8px;font-size:13px;color:#475569;flex-wrap:wrap}',
    // righe
    '.f1-righe-scroll{overflow-x:auto}',
    '.f1-righe{width:100%;border-collapse:collapse;min-width:640px}',
    '.f1-righe th{text-align:left;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.03em;color:#64748b;padding:4px 6px}',
    '.f1-righe td{padding:3px 4px;vertical-align:middle}',
    '.f1-riga-natura td{padding-top:0;padding-bottom:8px}',
    '.f1-num-cell{text-align:right;font-weight:600;white-space:nowrap}',
    '.f1-riga-btns{white-space:nowrap;text-align:right}',
    '.f1-ico{border:1px solid #e2e8f0;background:var(--shell-surface,#fff);border-radius:6px;width:26px;height:26px;cursor:pointer;color:#475569;font:inherit;font-size:12px;margin-left:2px}',
    '.f1-ico:disabled{opacity:.35;cursor:default}',
    '.f1-ico.comp{border-color:#10b981;color:#065f46;background:#ecfdf5}',
    '.f1-del{border:0;background:transparent;color:#cbd5e1;cursor:pointer;font-size:15px;font:inherit}',
    '.f1-del:hover{color:#ef4444}',
    '.f1-del:disabled{opacity:.3;cursor:default}',
    '.f1-del:disabled:hover{color:#cbd5e1}',
    // rate multiple (card Pagamento): griglia con header, somma con badge
    '.f1-rate-min{min-width:640px}',
    // IBAN uscito dalla riga rata (unico per tutte, sopra la lista): 5 colonne
    '.f1-rate-grid{display:grid;grid-template-columns:20px minmax(170px,1.6fr) 145px 130px 26px;gap:6px;align-items:center;margin-bottom:6px}',
    // colonna importo IVA di riga: indicativa, tono più tenue del totale
    '.f1-iva-imp{color:#64748b}',
    // voce evidenziata con le frecce nella tendina indirizzi
    '.f1-combo-item.sel{background:#e0f2fe}',
    '.f1-rate-h{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.03em;color:#64748b}',
    '.f1-rate-actions{display:flex;gap:8px;margin-top:8px;flex-wrap:wrap}',
    '.f1-rate-somma{display:flex;gap:10px;align-items:center;margin-top:8px;font-size:13px;flex-wrap:wrap}',
    // autofill cliente: campo+bottone affiancati, evidenza campi compilati da lookup, pannello IndicePA
    '.f1-in-row{display:flex;gap:6px;align-items:center}',
    '.f1-in-row .f1-btn{white-space:nowrap;flex-shrink:0}',
    '.f1-in-hl{border-color:#10b981;background:#ecfdf5}',
    '.f1-pa-res{grid-column:1/-1;border:1px solid #e2e8f0;border-radius:8px;max-height:220px;overflow:auto;background:var(--shell-surface,#fff)}',
    '.f1-pa-res-h{display:flex;justify-content:space-between;align-items:center;gap:8px;padding:6px 10px;font-size:12px;font-weight:700;color:#475569;border-bottom:1px solid #f1f5f9;position:sticky;top:0;background:inherit}',
    // sezioni avanzate
    '.f1-avanzate-t{font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#64748b;margin:18px 0 8px}',
    '.f1-sez{background:var(--shell-surface,#fff);border:1px solid #e2e8f0;border-radius:12px;margin-bottom:8px;overflow:hidden}',
    '.f1-sez-h{display:flex;align-items:center;gap:8px;width:100%;text-align:left;background:transparent;border:0;padding:11px 14px;cursor:pointer;font:inherit;font-weight:600;color:var(--shell-text,#0f172a)}',
    '.f1-sez-h:hover{background:#f8fafc}',
    '.f1-sez-arrow{color:#94a3b8;font-size:12px;width:12px}',
    '.f1-sez-body{padding:4px 14px 14px}',
    '.f1-sublist{margin-bottom:12px}',
    '.f1-sublist-t{font-size:12px;font-weight:700;color:#475569;margin:10px 0 6px}',
    '.f1-subrow{display:flex;gap:6px;align-items:flex-start;margin-bottom:6px}',
    '.f1-subgrid{display:grid;grid-template-columns:2fr 1fr 1fr 1fr 1fr 1fr auto auto;gap:6px;align-items:center;margin-bottom:6px}',
    '@media(max-width:900px){.f1-subgrid{grid-template-columns:1fr 1fr;}}',
    // riepilogo
    '.f1-tot-row{display:flex;justify-content:space-between;gap:10px;padding:5px 0;font-size:13px;border-bottom:1px dashed #f1f5f9}',
    '.f1-tot-row.sub{color:#64748b;font-size:12px;padding-left:10px}',
    '.f1-tot-row.big{font-size:14px}',
    '.f1-tot-row.netto{font-size:15px;border-bottom:0;margin-top:4px;padding-top:8px;border-top:2px solid var(--shell-primary-strong,#0e7490)}',
    '.f1-bollo-box{margin-top:10px;display:flex;flex-direction:column;gap:4px}',
    // stati
    '.f1-state{background:var(--shell-surface,#fff);border:1px solid #e2e8f0;border-radius:12px;padding:24px;text-align:center;color:#94a3b8;font-size:13px}',
    '.f1-state-err{border-color:#fecaca;background:#fef2f2;color:#b91c1c}',
    // overlay/modali/drawer
    '.f1-overlay{position:fixed;inset:0;background:rgba(15,23,42,.45);z-index:9000;display:flex;align-items:center;justify-content:center;padding:16px}',
    '.f1-overlay.right{justify-content:flex-end;align-items:stretch;padding:0}',
    '.f1-modal{background:var(--shell-surface,#fff);border-radius:14px;max-width:760px;width:100%;max-height:92vh;display:flex;flex-direction:column;box-shadow:0 20px 60px rgba(15,23,42,.3)}',
    '.f1-modal.xml{max-width:900px}',
    '.f1-drawer{background:var(--shell-surface,#fff);width:420px;max-width:94vw;height:100%;display:flex;flex-direction:column;box-shadow:-12px 0 40px rgba(15,23,42,.25)}',
    '.f1-modal-h{display:flex;justify-content:space-between;align-items:center;padding:14px 16px;border-bottom:1px solid #e2e8f0;font-weight:700}',
    '.f1-modal-b{padding:14px 16px;overflow:auto;flex:1}',
    '.f1-modal-f{padding:12px 16px;border-top:1px solid #e2e8f0;display:flex;gap:8px}',
    '.f1-x{border:0;background:transparent;cursor:pointer;font-size:15px;color:#64748b;font:inherit}',
    '.f1-x:hover{color:#ef4444}',
    '.f1-xml{font:12px/1.5 ui-monospace,Consolas,monospace;white-space:pre-wrap;word-break:break-all;background:#0f172a;color:#e2e8f0;border-radius:10px;padding:14px;margin:0;overflow:auto;max-height:65vh}',
    // impostazioni
    '.f1-prof-row{display:flex;gap:10px;padding:6px 0;border-bottom:1px dashed #f1f5f9;font-size:13px}',
    '.f1-prof-row b{min-width:200px;color:#64748b;font-weight:600}',
    '.f1-warns{background:#fffbeb;border:1px solid #fde68a;border-radius:10px;padding:10px 12px;margin-bottom:12px;color:#92400e;font-size:13px}',
    '.f1-help-ol{margin:8px 0 0;padding-left:20px;font-size:13px;color:#475569;line-height:1.7}',
    '.f1-help-ol code{background:#f1f5f9;border-radius:4px;padding:1px 5px;font-size:12px}',
    '.f1-warns li{margin-left:16px}',
    '.f1-check-list{font-size:13px;color:#475569;line-height:1.8}',
    // toast
    '.f1-toast{position:fixed;bottom:20px;left:50%;transform:translateX(-50%) translateY(20px);background:#0f172a;color:#fff;padding:10px 18px;border-radius:10px;opacity:0;transition:.3s;z-index:99999;font-size:13px}',
    '.f1-toast.f1-show{opacity:1;transform:translateX(-50%) translateY(0)}',
    '.f1-toast-err{background:#b91c1c}',
    // barra ricerca liste
    '.f1-listbar{display:flex;gap:8px;margin-bottom:12px;align-items:center;flex-wrap:wrap}',
    '.f1-listbar .f1-in{max-width:280px}',
  ].join('\n');
}

function injectStyle(doc) {
  if (doc.getElementById(STYLE_ID)) return;
  const st = doc.createElement('style');
  st.id = STYLE_ID;
  st.textContent = css();
  doc.head.appendChild(st);
}

// Toast su document.body (pattern sop-v3): sopravvive ai re-render, è la
// risposta visibile minima di ogni azione.
function makeToast(doc) {
  return function toast(msg, kind) {
    const t = doc.createElement('div');
    t.className = 'f1-toast f1-toast-' + (kind || 'ok');
    t.textContent = msg;
    doc.body.appendChild(t);
    setTimeout(() => { t.classList.add('f1-show'); }, 10);
    setTimeout(() => { t.classList.remove('f1-show'); setTimeout(() => { t.remove(); }, 300); }, 3400);
  };
}

const TAB_LABELS = {
  nuova: 'Nuova fattura',
  emesse: 'Emesse',
  clienti: 'Clienti',
  voci: 'Voci',
  impostazioni: 'Impostazioni',
};

// nome cliente da una testata di lista (campi difensivi: la lista porta le
// testate senza righe; il nome può arrivare come snapshot o campo piatto)
function fattClienteNome(f) {
  if (f.cessionarioNome) return f.cessionarioNome; // campo delle testate in lista (fatt-store)
  if (f.cessionario) return nomeCliente(f.cessionario);
  if (f.cliente) return nomeCliente(f.cliente);
  return f.clienteNome || f.clienteDenominazione || '—';
}

// ---------------------------------------------------------------------------
// VoceForm — form minimale del catalogo voci (modale)
// ---------------------------------------------------------------------------
function VoceForm({ win, voce, onSaved, onClose, toast }) {
  const nuova = !voce || !voce.id;
  const [v, setV] = React.useState(() => Object.assign({
    titolo: '', descrizione: '', unitaMisura: '', prezzoUnitario: null, aliquotaIva: 22, natura: '',
  }, voce || {}));
  const [saving, setSaving] = React.useState(false);
  const savingRef = React.useRef(false);
  const dirtyRef = React.useRef(false);

  function set(campo, val) { dirtyRef.current = true; setV((p) => Object.assign({}, p, { [campo]: val })); }
  const tryClose = React.useCallback(() => {
    if (dirtyRef.current && !win.confirm('Chiudere senza salvare la voce?')) return;
    onClose();
  }, [win, onClose]);
  React.useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') { e.stopPropagation(); tryClose(); } }
    win.document.addEventListener('keydown', onKey, true);
    return () => win.document.removeEventListener('keydown', onKey, true);
  }, [win, tryClose]);

  async function salva() {
    if (savingRef.current) return;
    if (!(v.titolo || '').trim()) { toast('Titolo obbligatorio', 'err'); return; }
    savingRef.current = true; setSaving(true);
    try {
      let dto;
      if (nuova) dto = await api(win, '/voci', { method: 'POST', body: v });
      else dto = await api(win, '/voci/' + encodeURIComponent(voce.id), {
        method: 'PATCH', body: Object.assign({}, v, { _expectedUpdatedAt: voce.updatedAt }),
      });
      toast(nuova ? 'Voce creata' : 'Voce aggiornata');
      dirtyRef.current = false;
      onSaved(dto);
    } catch (e) {
      toast('NON salvata: ' + e.message, 'err');
    } finally {
      savingRef.current = false; setSaving(false);
    }
  }

  return (
    <div className="f1-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) tryClose(); }}>
      <div className="f1-modal" role="dialog" aria-label={nuova ? 'Nuova voce' : 'Modifica voce'} style={{ maxWidth: 560 }}>
        <div className="f1-modal-h">
          <span>{nuova ? 'Nuova voce' : 'Modifica voce'}</span>
          <button type="button" className="f1-x" title="Chiudi" onClick={tryClose}>✕</button>
        </div>
        <div className="f1-modal-b">
          <div className="f1-grid">
            <label className="f1-campo wide"><span className="f1-lbl">Titolo *</span>
              <input className="f1-in" value={v.titolo || ''} onChange={(e) => set('titolo', e.target.value)} placeholder="Es. Manodopera specializzata" />
            </label>
            <label className="f1-campo wide"><span className="f1-lbl">Descrizione in fattura</span>
              <textarea className="f1-in" rows={2} value={v.descrizione || ''} onChange={(e) => set('descrizione', e.target.value)} />
            </label>
            <label className="f1-campo"><span className="f1-lbl">Unità di misura</span>
              <input className="f1-in" value={v.unitaMisura || ''} onChange={(e) => set('unitaMisura', e.target.value)} placeholder="es. ore" />
            </label>
            <label className="f1-campo"><span className="f1-lbl">Prezzo unitario</span>
              <input className="f1-in num" type="number" step="any" value={v.prezzoUnitario == null ? '' : v.prezzoUnitario}
                onChange={(e) => set('prezzoUnitario', numOrNull(e.target.value))} />
            </label>
            <label className="f1-campo"><span className="f1-lbl">Aliquota IVA</span>
              <select className="f1-in" value={v.aliquotaIva == null ? 22 : v.aliquotaIva} onChange={(e) => set('aliquotaIva', Number(e.target.value))}>
                {ALIQUOTE_IVA.map((a) => <option key={a} value={a}>{a + '%'}</option>)}
              </select>
            </label>
            {Number(v.aliquotaIva) === 0 && (
              <label className="f1-campo"><span className="f1-lbl">Natura</span>
                <select className="f1-in" value={v.natura || ''} onChange={(e) => set('natura', e.target.value)}>
                  <option value="">— natura —</option>
                  {NATURE.map((n) => <option key={n.code} value={n.code}>{n.code + ' — ' + n.label}</option>)}
                </select>
              </label>
            )}
          </div>
        </div>
        <div className="f1-modal-f">
          <button type="button" className="f1-btn" disabled={saving} onClick={salva}>{saving ? 'Salvo…' : 'Salva voce'}</button>
          <button type="button" className="f1-btn gray" onClick={tryClose}>Annulla</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Impostazioni — pezzi del blocco "Collegamento a OpenAPI".
// Helper PURI + due componenti di presentazione: nessuna chiamata di rete qui,
// così la scheda resta leggibile anche quando il server non risponde.
// ---------------------------------------------------------------------------
const SEMAFORO = {
  verde: { colore: '#10b981', alone: 'rgba(16,185,129,.20)', titolo: 'Tutto pronto' },
  giallo: { colore: '#f59e0b', alone: 'rgba(245,158,11,.20)', titolo: 'Configurazione incompleta' },
  rosso: { colore: '#ef4444', alone: 'rgba(239,68,68,.20)', titolo: 'Non collegato' },
};

// Confronto indirizzi "umano": la query string (dove può viaggiare il segreto),
// lo slash finale e le maiuscole non fanno differenza.
function normalizzaUrl(u) {
  return String(u || '').trim().replace(/[?#].*$/, '').replace(/\/+$/, '').toLowerCase();
}
function stessoIndirizzo(a, b) {
  const na = normalizzaUrl(a);
  return !!na && na === normalizzaUrl(b);
}

function Pallino({ tipo, grande }) {
  const s = SEMAFORO[tipo] || SEMAFORO.rosso;
  const d = grande ? 18 : 11;
  return (
    <span aria-hidden="true" style={{
      width: d, height: d, borderRadius: '50%', background: s.colore, flexShrink: 0,
      display: 'inline-block', boxShadow: '0 0 0 ' + (grande ? 5 : 3) + 'px ' + s.alone,
    }} />
  );
}

// Riga "spunta o croce" della card Collegamento. `stato`: true = ok, false = manca.
function RigaCheck({ stato, testo, dettaglio, children }) {
  return (
    <div className="f1-prof-row" style={{ alignItems: 'flex-start' }}>
      <b style={{ minWidth: 20, color: stato ? '#047857' : '#b91c1c', fontWeight: 700 }}>
        {stato ? '✓' : '✕'}
      </b>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontWeight: 600 }}>{testo}</span>
        {dettaglio ? <span className="f1-muted" style={{ marginLeft: 6 }}>{dettaglio}</span> : null}
        {children}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principale
// ---------------------------------------------------------------------------
export function FattApp({ win, registerHandle }) {
  const doc = win.document;
  const toast = React.useMemo(() => makeToast(doc), [doc]);
  const [tab, setTab] = React.useState('emesse');
  // editorId: undefined = editor chiuso; null = nuova bozza; stringa = bozza esistente
  const [editorId, setEditorId] = React.useState(undefined);
  const alive = React.useRef(true);
  React.useEffect(() => () => { alive.current = false; }, []);

  // guardia dell'editor (registrata dall'Editor: conferma se ci sono modifiche
  // non salvate) — la navigazione tra tab NON perde mai dati in silenzio
  const editorGuardRef = React.useRef(null);
  const registerGuard = React.useCallback((fn) => { editorGuardRef.current = fn; }, []);
  function canLeaveEditor() {
    return !editorGuardRef.current || editorGuardRef.current() === true;
  }

  // ---- liste ----
  const [fatture, setFatture] = React.useState([]);
  const [fattLoading, setFattLoading] = React.useState(false);
  const [fattError, setFattError] = React.useState(null);
  const [fattQ, setFattQ] = React.useState('');
  const [clienti, setClienti] = React.useState([]);
  const [cliLoading, setCliLoading] = React.useState(false);
  const [cliError, setCliError] = React.useState(null);
  const [cliEditing, setCliEditing] = React.useState(undefined); // undefined chiuso, null nuovo, DTO modifica
  const [voci, setVoci] = React.useState([]);
  const [vociLoading, setVociLoading] = React.useState(false);
  const [vociError, setVociError] = React.useState(null);
  const [voceEditing, setVoceEditing] = React.useState(undefined);
  const [profilo, setProfilo] = React.useState(null);
  const [companyStato, setCompanyStato] = React.useState(null);
  const [profLoading, setProfLoading] = React.useState(false);
  const [profError, setProfError] = React.useState(null);
  // stato del collegamento a OpenAPI (scheda Impostazioni): caricamento/errore
  // PROPRI, così un profilo cedente lento o rotto non nasconde il semaforo.
  const [cfg, setCfg] = React.useState(null);
  const [cfgLoading, setCfgLoading] = React.useState(false);
  const [cfgError, setCfgError] = React.useState(null);
  const [setupBusy, setSetupBusy] = React.useState(false);
  const setupRef = React.useRef(false);       // guardia sincrona anti doppio-click
  const webhookRef = React.useRef(null);      // per il fallback di copia (select+execCommand)

  const loadFatture = React.useCallback(async (q) => {
    setFattLoading(true); setFattError(null);
    try {
      const query = q != null ? q : '';
      const res = await api(win, '/fatture?direction=out' + (query ? '&q=' + encodeURIComponent(query) : ''));
      if (alive.current) setFatture((res && res.fatture) || []);
    } catch (e) {
      if (alive.current) setFattError(e.message);
    } finally {
      if (alive.current) setFattLoading(false);
    }
  }, [win]);
  const debSearch = React.useMemo(() => debounce((q) => { loadFatture(q); }, 350), [loadFatture]);
  React.useEffect(() => () => debSearch.cancel(), [debSearch]);

  const loadClienti = React.useCallback(async () => {
    setCliLoading(true); setCliError(null);
    try {
      const res = await api(win, '/clienti');
      if (alive.current) setClienti((res && res.clienti) || []);
    } catch (e) {
      if (alive.current) setCliError(e.message);
    } finally {
      if (alive.current) setCliLoading(false);
    }
  }, [win]);

  const loadVoci = React.useCallback(async () => {
    setVociLoading(true); setVociError(null);
    try {
      const res = await api(win, '/voci');
      if (alive.current) setVoci((res && res.voci) || []);
    } catch (e) {
      if (alive.current) setVociError(e.message);
    } finally {
      if (alive.current) setVociLoading(false);
    }
  }, [win]);

  const loadProfilo = React.useCallback(async () => {
    setProfLoading(true); setProfError(null);
    try {
      const res = await api(win, '/profilo-cedente');
      if (alive.current) setProfilo(res || null);
    } catch (e) {
      if (alive.current) setProfError(e.message);
    } finally {
      if (alive.current) setProfLoading(false);
    }
    // stato del servizio Company (lookup SDI/PEC a pagamento) — non bloccante
    try {
      const st = await api(win, '/lookup-azienda-stato');
      if (alive.current) setCompanyStato(st || null);
    } catch (e) { /* stato ignoto: la card mostra 'non attiva' */ }
  }, [win]);

  // Stato del collegamento a OpenAPI. Gli endpoint /config/* possono non essere
  // ancora installati sul server: 404/501 NON è un errore da mostrare come tale,
  // è semplicemente "non collegato" (la scheda resta leggibile e spiega cosa fare).
  const loadConfigStato = React.useCallback(async () => {
    setCfgLoading(true); setCfgError(null);
    try {
      const res = await api(win, '/config/stato');
      if (alive.current) setCfg(res || null);
    } catch (e) {
      if (!alive.current) return;
      if (e && (e.status === 404 || e.status === 501)) {
        setCfg({
          nonDisponibile: true,
          tokenConfigurato: false, ambiente: null,
          webhookConfigurato: false, webhookUrl: '',
          remota: null,
          problemi: ['Il collegamento a OpenAPI non è ancora attivo su questo server: la funzione di invio non è installata.'],
        });
      } else {
        setCfgError(e.message || 'Errore');
      }
    } finally {
      if (alive.current) setCfgLoading(false);
    }
  }, [win]);

  const reloadCurrent = React.useCallback(() => {
    if (tab === 'emesse' || tab === 'nuova') loadFatture(fattQ);
    if (tab === 'clienti') loadClienti();
    if (tab === 'voci') loadVoci();
    if (tab === 'impostazioni') { loadConfigStato(); loadProfilo(); }
  }, [tab, fattQ, loadFatture, loadClienti, loadVoci, loadProfilo, loadConfigStato]);

  // caricamento all'ingresso in ogni tab (sempre dal server)
  React.useEffect(() => {
    if (tab === 'emesse') loadFatture(fattQ);
    else if (tab === 'clienti') loadClienti();
    else if (tab === 'voci') loadVoci();
    else if (tab === 'impostazioni') { loadConfigStato(); loadProfilo(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // ---- navigazione (tab interne = nav della register) ----
  const goTabRef = React.useRef(null);
  goTabRef.current = function goTab(t) {
    if (TAB_IDS.indexOf(t) === -1) return false;
    if (editorId !== undefined && !canLeaveEditor()) return true; // l'utente resta nell'editor
    if (t === 'nuova') {
      setEditorId(null);
      setTab('nuova');
    } else {
      setEditorId(undefined);
      setTab(t);
    }
    return true;
  };

  React.useEffect(() => {
    registerHandle({
      setTab: (t) => goTabRef.current(t),
      reload: () => reloadCurrent(),
      canLeave: () => canLeaveEditor(),
    });
    return () => registerHandle(null);
  }, [registerHandle, reloadCurrent]); // eslint-disable-line react-hooks/exhaustive-deps

  function apriEditor(id) {
    if (editorId !== undefined && !canLeaveEditor()) return;
    setEditorId(id);
  }
  function chiudiEditor() {
    setEditorId(undefined);
    if (tab === 'nuova') setTab('emesse');
    loadFatture(fattQ); // la lista riflette SEMPRE lo stato server dopo l'editor
  }

  // ---- viste ----
  function renderEmesse() {
    const columns = [
      {
        key: 'numero', label: 'Numero',
        render: (f) => f.numeroCustom || f.numero
          ? <b>{String(f.numeroCustom || f.numero) + (f.serie ? '/' + f.serie : '')}</b>
          : <span className="f1-badge-stato bozza">bozza</span>,
      },
      { key: 'cliente', label: 'Cliente', render: (f) => fattClienteNome(f) },
      { key: 'dataEmissione', label: 'Data', render: (f) => fmtDay(f.dataEmissione || f.data_emissione) },
      { key: 'totaleDocumento', label: 'Totale', align: 'right', render: (f) => <b>{fmtEuro(f.totaleDocumento != null ? f.totaleDocumento : f.totale_documento)}</b> },
      {
        key: 'stato', label: 'Stato',
        render: (f) => <span className="f1-badge-stato bozza">{STATO_LABEL[f.stato] || f.stato || 'Bozza'}</span>,
      },
    ];
    return (
      <React.Fragment>
        <div className="f1-listbar">
          <button type="button" className="f1-btn" onClick={() => { if (canLeaveEditor()) { setEditorId(null); setTab('nuova'); } }}>+ Nuova fattura</button>
          <input className="f1-in" placeholder="Cerca (numero, cliente)…" value={fattQ}
            onChange={(e) => { const q = e.target.value; setFattQ(q); debSearch(q); }} />
        </div>
        <DataTable
          columns={columns}
          rows={fatture}
          rowKey="id"
          loading={fattLoading}
          error={fattError}
          onRetry={() => loadFatture(fattQ)}
          onRowClick={(f) => apriEditor(f.id)}
          emptyMessage="Nessuna fattura. Crea la prima bozza con «+ Nuova fattura»."
        />
      </React.Fragment>
    );
  }

  function renderClienti() {
    const columns = [
      { key: 'nome', label: 'Cliente', render: (c) => <b>{nomeCliente(c)}</b> },
      { key: 'partitaIva', label: 'P.IVA / CF', render: (c) => c.partitaIva || c.codiceFiscale || '—' },
      {
        key: 'sdi', label: 'Recapito SDI',
        render: (c) => c.codiceDestinatario || (c.pec ? 'PEC: ' + c.pec : <span className="f1-muted">0000000 (area riservata)</span>),
      },
      { key: 'comune', label: 'Comune', render: (c) => (c.comune || '—') + (c.provincia ? ' (' + c.provincia + ')' : '') },
      {
        key: 'flag', label: '', align: 'right',
        render: (c) => (
          <span>
            {!!c.isPa && <span className="f1-pill" style={{ marginRight: 4 }}>PA</span>}
            {!!c.splitPayment && <span className="f1-pill">split</span>}
            <button type="button" className="f1-del" title="Archivia cliente" onClick={() => archiviaCliente(c)}>✕</button>
          </span>
        ),
      },
    ];
    return (
      <React.Fragment>
        <div className="f1-listbar">
          <button type="button" className="f1-btn" onClick={() => setCliEditing(null)}>+ Nuovo cliente</button>
        </div>
        <DataTable
          columns={columns}
          rows={clienti}
          rowKey="id"
          loading={cliLoading}
          error={cliError}
          onRetry={loadClienti}
          onRowClick={(c) => setCliEditing(c)}
          emptyMessage="Nessun cliente in anagrafica. Crea il primo con «+ Nuovo cliente»."
        />
      </React.Fragment>
    );
  }

  async function archiviaCliente(c) {
    if (!win.confirm('Archiviare il cliente «' + nomeCliente(c) + '»? Le fatture esistenti restano.')) return;
    try {
      await api(win, '/clienti/' + encodeURIComponent(c.id), { method: 'DELETE' });
      toast('Cliente archiviato');
      loadClienti();
    } catch (e) { toast('Errore: ' + e.message, 'err'); }
  }

  function renderVoci() {
    const columns = [
      { key: 'titolo', label: 'Voce', render: (v) => <b>{v.titolo}</b> },
      { key: 'descrizione', label: 'Descrizione', render: (v) => <span className="f1-muted">{v.descrizione || '—'}</span> },
      { key: 'unitaMisura', label: 'U.M.' },
      { key: 'prezzoUnitario', label: 'Prezzo', align: 'right', render: (v) => fmtEuro(v.prezzoUnitario) },
      { key: 'aliquotaIva', label: 'IVA', render: (v) => (v.aliquotaIva != null ? v.aliquotaIva + '%' : '—') + (v.natura ? ' · ' + v.natura : '') },
      { key: 'usoCount', label: 'Usi', align: 'right' },
      {
        key: 'az', label: '', align: 'right',
        render: (v) => <button type="button" className="f1-del" title="Elimina voce" onClick={() => eliminaVoce(v)}>✕</button>,
      },
    ];
    return (
      <React.Fragment>
        <div className="f1-listbar">
          <button type="button" className="f1-btn" onClick={() => setVoceEditing(null)}>+ Nuova voce</button>
          <span className="f1-muted">Le voci alimentano i suggerimenti della descrizione righe nell&rsquo;editor.</span>
        </div>
        <DataTable
          columns={columns}
          rows={voci}
          rowKey="id"
          loading={vociLoading}
          error={vociError}
          onRetry={loadVoci}
          onRowClick={(v) => setVoceEditing(v)}
          emptyMessage="Nessuna voce di catalogo. Crea la prima con «+ Nuova voce»."
        />
      </React.Fragment>
    );
  }

  async function eliminaVoce(v) {
    if (!win.confirm('Eliminare la voce «' + v.titolo + '»?')) return;
    try {
      await api(win, '/voci/' + encodeURIComponent(v.id), { method: 'DELETE' });
      toast('Voce eliminata');
      loadVoci();
    } catch (e) { toast('Errore: ' + e.message, 'err'); }
  }

  // ---- Impostazioni: azioni ----------------------------------------------
  // Crea/aggiorna la configurazione azienda su OpenAPI (e i callback che
  // puntano al NOSTRO webhook). Guardia sincrona: due click = una sola chiamata.
  async function eseguiSetupOpenapi() {
    if (setupRef.current) return;
    setupRef.current = true; setSetupBusy(true);
    try {
      const res = await api(win, '/config/openapi-setup', { method: 'POST', body: {} });
      toast(res && res.azione === 'aggiornata'
        ? 'Configurazione aggiornata su OpenAPI'
        : 'Azienda configurata su OpenAPI');
      await loadConfigStato();
    } catch (e) {
      toast('NON riuscito: ' + (e.message || 'errore sconosciuto'), 'err');
    } finally {
      setupRef.current = false;
      if (alive.current) setSetupBusy(false);
    }
  }

  // Copia dell'indirizzo webhook: clipboard moderna, poi fallback select+copy,
  // poi — se anche quello fallisce — si DICE all'utente cosa fare a mano.
  async function copiaWebhook(url) {
    if (!url) { toast('Nessun indirizzo da copiare', 'err'); return; }
    try {
      const nav = win.navigator;
      if (nav && nav.clipboard && nav.clipboard.writeText) {
        await nav.clipboard.writeText(url);
        toast('Indirizzo copiato');
        return;
      }
    } catch (e) { /* permesso negato o pagina non sicura: si prova il fallback */ }
    const el = webhookRef.current;
    if (el) {
      try {
        el.focus(); el.select();
        if (doc.execCommand && doc.execCommand('copy')) { toast('Indirizzo copiato'); return; }
      } catch (e) { /* fallback non disponibile: sotto si spiega la via manuale */ }
    }
    toast('Copia non riuscita: seleziona l’indirizzo e premi Ctrl+C', 'err');
  }

  function renderImpostazioni() {
    // ---- lettura difensiva dello stato collegamento ------------------------
    const remota = (cfg && cfg.remota) || null;
    const trovata = !!(remota && remota.trovata);
    const callbacks = (remota && remota.callbacks) || [];
    const webhookUrl = (cfg && cfg.webhookUrl) || '';
    const callbackOk = trovata && callbacks.length > 0
      && callbacks.every((c) => stessoIndirizzo(c && c.url, webhookUrl));
    const problemi = (cfg && cfg.problemi) || [];
    const ambiente = (cfg && cfg.ambiente) || null;
    const test = ambiente === 'test';
    const emissione = !!(remota && remota.customerInvoice);
    const ricezione = !!(remota && remota.supplierInvoice);
    const tuttoPronto = !!cfg && !!cfg.tokenConfigurato && !!cfg.webhookConfigurato
      && trovata && emissione && ricezione && callbackOk && problemi.length === 0;
    const semaforo = (!cfg || !cfg.tokenConfigurato) ? 'rosso' : (tuttoPronto ? 'verde' : 'giallo');
    const puoConfigurare = !!(cfg && cfg.tokenConfigurato);
    const serveSetup = puoConfigurare && (!trovata || !callbackOk || !emissione || !ricezione);

    const badgeAmbiente = !ambiente ? null : (test ? (
      <span className="f1-pill" style={{
        background: '#ffedd5', color: '#9a3412', border: '1px solid #fb923c',
        padding: '3px 10px', whiteSpace: 'normal', lineHeight: 1.4,
      }}>
        AMBIENTE DI PROVA — le fatture NON vanno allo SDI reale
      </span>
    ) : (
      <span className="f1-pill ok" style={{ padding: '3px 10px' }}>
        PRODUZIONE — le fatture vengono inviate davvero
      </span>
    ));

    // ---- cosa deve fare l'utente, in ordine --------------------------------
    const passi = [];
    if (cfg && !cfg.tokenConfigurato) {
      passi.push(<span>Crea il token su <b>console.openapi.com</b> → APIs → <b>Invoice</b> → sezione <b>OAuth</b>, poi scrivilo nel file <code>server/.env</code> come <code>FATT_OPENAPI_TOKEN=&lt;token&gt;</code> e riavvia il server.</span>);
    }
    if (cfg && !cfg.webhookConfigurato) {
      passi.push(<span>Scegli una frase lunga a caso e mettila nel file <code>server/.env</code> come <code>FATT_WEBHOOK_SECRET=&lt;frase&gt;</code>, poi riavvia il server: serve a distinguere le notifiche vere dai falsi.</span>);
    }
    if (puoConfigurare && !trovata) {
      passi.push(<span>Premi <b>Configura ora</b> qui sotto: registra la tua azienda su OpenAPI e imposta da solo l’indirizzo delle notifiche.</span>);
    }
    if (trovata && !callbackOk) {
      passi.push(<span>Premi <b>Aggiorna configurazione</b>: oggi le notifiche di OpenAPI non arrivano a questa applicazione.</span>);
    }
    if (trovata && !emissione) {
      passi.push(<span>Attiva l’<b>emissione</b> (invio delle tue fatture): la imposta lo stesso bottone qui sotto.</span>);
    }
    if (trovata && !ricezione) {
      passi.push(<span>Attiva la <b>ricezione</b> delle fatture dei fornitori: oltre al bottone qui sotto serve registrare il codice destinatario presso l’Agenzia delle Entrate (guida sul sito di OpenAPI).</span>);
    }
    if (test) {
      passi.push(<span>Quando le prove sono finite, passa in produzione: in <code>server/.env</code> metti <code>FATT_OPENAPI_ENV=production</code> con il token di produzione e riavvia. Finché resta <code>test</code> nessuna fattura raggiunge davvero lo SDI.</span>);
    }

    const bottoneSetup = (
      <button type="button" className="f1-btn" disabled={setupBusy || !puoConfigurare}
        onClick={eseguiSetupOpenapi}>
        {setupBusy ? 'Configuro…' : (trovata ? 'Aggiorna configurazione' : 'Configura ora')}
      </button>
    );

    // ---- riga di stato compatta in cima ------------------------------------
    function statoCompatto() {
      const barra = { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 14px' };
      if (cfgError) {
        return (
          <div className="f1-card" style={barra}>
            <Pallino tipo="rosso" />
            <b>Stato del collegamento non disponibile</b>
            <span className="f1-muted">{cfgError}</span>
            <button type="button" className="f1-btn gray sm" style={{ marginLeft: 'auto' }} onClick={loadConfigStato}>Riprova</button>
          </div>
        );
      }
      if (!cfg) {
        return (
          <div className="f1-card" style={barra}>
            <span className="f1-muted">{cfgLoading ? 'Verifico il collegamento a OpenAPI…' : 'Collegamento a OpenAPI: stato sconosciuto.'}</span>
            {!cfgLoading && <button type="button" className="f1-btn gray sm" style={{ marginLeft: 'auto' }} onClick={loadConfigStato}>Verifica</button>}
          </div>
        );
      }
      return (
        <div className="f1-card" style={barra}>
          <Pallino tipo={semaforo} />
          <b>OpenAPI: {SEMAFORO[semaforo].titolo}</b>
          {badgeAmbiente}
          {cfgLoading && <span className="f1-muted">aggiorno…</span>}
          <button type="button" className="f1-btn gray sm" style={{ marginLeft: 'auto' }} disabled={cfgLoading} onClick={loadConfigStato}>
            {cfgLoading ? 'Controllo…' : 'Ricontrolla'}
          </button>
        </div>
      );
    }

    return (
      <React.Fragment>
        {statoCompatto()}

        {/* checklist numerata: compare SOLO se c'è davvero qualcosa da fare */}
        {cfg && !cfgError && passi.length > 0 && (
          <div className="f1-card">
            <div className="f1-card-t">Cosa fare, nell’ordine</div>
            <ol className="f1-help-ol">
              {passi.map((p, i) => <li key={i}>{p}</li>)}
            </ol>
          </div>
        )}

        {/* 1) Collegamento a OpenAPI */}
        <div className="f1-card">
          <div className="f1-card-t">Collegamento a OpenAPI</div>
          {cfgError ? (
            <div className="f1-state f1-state-err">
              <div>⚠ {cfgError}</div>
              <button type="button" className="f1-btn" style={{ marginTop: 10 }} onClick={loadConfigStato}>Riprova</button>
            </div>
          ) : !cfg ? (
            <div className="f1-state">
              {cfgLoading ? 'Controllo il collegamento…' : (
                <React.Fragment>
                  <div>Stato del collegamento non ancora verificato.</div>
                  <button type="button" className="f1-btn" style={{ marginTop: 10 }} onClick={loadConfigStato}>Verifica adesso</button>
                </React.Fragment>
              )}
            </div>
          ) : (
            <React.Fragment>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
                <Pallino tipo={semaforo} grande />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 17 }}>{SEMAFORO[semaforo].titolo}</div>
                  <div className="f1-muted">
                    {semaforo === 'verde' && 'L’applicazione parla con OpenAPI e riceve le notifiche.'}
                    {semaforo === 'giallo' && 'Manca ancora qualcosa: qui sotto è segnato con una croce.'}
                    {semaforo === 'rosso' && 'L’applicazione non è collegata a OpenAPI: le fatture restano qui.'}
                  </div>
                </div>
              </div>

              <RigaCheck stato={!!cfg.tokenConfigurato} testo="Token presente"
                dettaglio={cfg.tokenConfigurato
                  ? '— la chiave per parlare con OpenAPI è impostata sul server'
                  : '— senza la chiave non si può inviare né ricevere nulla'} />
              <RigaCheck stato={!!ambiente}
                testo={'Ambiente: ' + (ambiente === 'production' ? 'produzione' : (ambiente === 'test' ? 'prova (test)' : 'sconosciuto'))}>
                {badgeAmbiente ? <span style={{ display: 'block', marginTop: 5 }}>{badgeAmbiente}</span> : null}
              </RigaCheck>
              <RigaCheck stato={!!cfg.webhookConfigurato} testo="Segreto delle notifiche impostato"
                dettaglio={cfg.webhookConfigurato
                  ? '— le notifiche di OpenAPI vengono riconosciute'
                  : '— senza segreto ogni notifica viene rifiutata'} />
              <RigaCheck stato={trovata} testo="Azienda configurata su OpenAPI"
                dettaglio={trovata
                  ? ('— P.IVA ' + (remota.fiscalId || '—'))
                  : '— l’azienda non risulta ancora configurata su OpenAPI'} />
              <RigaCheck stato={emissione} testo="Emissione attiva"
                dettaglio={emissione ? '— possiamo inviare le fatture ai clienti' : '— l’invio delle fatture è spento'} />
              <RigaCheck stato={ricezione} testo="Ricezione attiva"
                dettaglio={ricezione ? '— riceviamo le fatture dei fornitori' : '— le fatture dei fornitori non arrivano in automatico'} />
              <RigaCheck stato={callbackOk} testo="Notifiche indirizzate a questa applicazione"
                dettaglio={callbackOk
                  ? ('— ' + callbacks.length + (callbacks.length === 1 ? ' notifica collegata' : ' notifiche collegate'))
                  : (trovata ? '— gli avvisi di OpenAPI non arrivano qui' : '— si impostano insieme all’azienda')} />

              {/* avvisi che spiegano PERCHE' il bottone serve */}
              {!trovata && puoConfigurare && (
                <div className="f1-banner warn" style={{ marginTop: 12, marginBottom: 8 }}>
                  L’azienda non risulta ancora configurata su OpenAPI: finché è così nessuna fattura può partire.
                </div>
              )}
              {trovata && !callbackOk && (
                <div className="f1-banner warn" style={{ marginTop: 12, marginBottom: 8 }}>
                  Le notifiche di OpenAPI non puntano a questa applicazione: gli esiti delle fatture non arriverebbero.
                  {callbacks.length > 0 && (
                    <ul style={{ margin: '6px 0 0 18px' }}>
                      {callbacks.map((c, i) => (
                        <li key={i} style={{ wordBreak: 'break-all' }}>
                          {(c && c.evento) || 'evento'}: {(c && c.url) || '—'}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {!puoConfigurare && (
                <div className="f1-banner err" style={{ marginTop: 12, marginBottom: 8 }}>
                  Senza token non si può configurare nulla: si comincia dal punto 1 della lista qui sopra.
                </div>
              )}
              {problemi.map((p, i) => (
                <div key={'pr' + i} className="f1-banner warn" style={{ marginBottom: 6 }}>⚠ {p}</div>
              ))}

              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                {tuttoPronto
                  ? <span className="f1-muted">Niente da fare: il collegamento è completo.</span>
                  : bottoneSetup}
                {!tuttoPronto && !serveSetup && puoConfigurare && (
                  <span className="f1-muted">Rilancia la configurazione se qualcosa non torna.</span>
                )}
              </div>
            </React.Fragment>
          )}
        </div>

        {/* 2) Indirizzo per le notifiche (webhook) */}
        <div className="f1-card">
          <div className="f1-card-t">Indirizzo per le notifiche (webhook)</div>
          {cfgError ? (
            <div className="f1-state f1-state-err">
              <div>⚠ {cfgError}</div>
              <button type="button" className="f1-btn" style={{ marginTop: 10 }} onClick={loadConfigStato}>Riprova</button>
            </div>
          ) : !cfg ? (
            <div className="f1-state">{cfgLoading ? 'Caricamento…' : 'Indirizzo non ancora verificato.'}</div>
          ) : (
            <React.Fragment>
              <div className="f1-in-row">
                <input className="f1-in" readOnly ref={webhookRef} aria-label="Indirizzo per le notifiche"
                  value={webhookUrl || '(non disponibile)'}
                  onFocus={(e) => { try { e.target.select(); } catch (err) { /* selezione non critica */ } }} />
                <button type="button" className="f1-btn gray" disabled={!webhookUrl}
                  onClick={() => copiaWebhook(webhookUrl)}>Copia</button>
              </div>
              <span className="f1-hint" style={{ marginTop: 6 }}>
                È l’indirizzo che OpenAPI chiama per dirci com’è andata una fattura. Il bottone «Configura ora» qui sopra lo imposta da solo: non serve incollarlo a mano nel portale.
              </span>
              {!cfg.webhookConfigurato && (
                <div className="f1-banner err" style={{ marginTop: 10, marginBottom: 0 }}>
                  Manca il segreto: senza, le notifiche verrebbero rifiutate.
                </div>
              )}
              {!webhookUrl && (
                <div className="f1-banner warn" style={{ marginTop: 10, marginBottom: 0 }}>
                  Il server non espone ancora un indirizzo pubblico per le notifiche.
                </div>
              )}
            </React.Fragment>
          )}
        </div>

        {/* 3) le card già esistenti, in fondo: profilo cedente + servizio Company */}
        {profError ? (
          <div className="f1-card">
            <div className="f1-card-t">Profilo cedente (sola lettura)</div>
            <div className="f1-state f1-state-err">
              <div>⚠ {profError}</div>
              <button type="button" className="f1-btn" style={{ marginTop: 10 }} onClick={loadProfilo}>Riprova</button>
            </div>
          </div>
        ) : (profLoading && !profilo) ? (
          <div className="f1-card">
            <div className="f1-card-t">Profilo cedente (sola lettura)</div>
            <div className="f1-state">Caricamento profilo cedente…</div>
          </div>
        ) : !profilo ? (
          <div className="f1-card">
            <div className="f1-card-t">Profilo cedente (sola lettura)</div>
            <div className="f1-state">Profilo non disponibile.</div>
          </div>
        ) : renderCardStoriche()}
      </React.Fragment>
    );
  }

  // Card storiche della scheda Impostazioni (profilo cedente + servizio Company):
  // contenuto identico a prima, spostato in fondo.
  function renderCardStoriche() {
    const p = profilo;
    const sede = p.sede || {};
    const warnings = p.warnings || [];
    return (
      <React.Fragment>
        {warnings.length > 0 && (
          <div className="f1-warns">
            <b>Dati mancanti nel profilo azienda</b> (si completano in Amministrazione → Profilo azienda):
            <ul>{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          </div>
        )}
        {/* stato del lookup a pagamento: il bottone "Cerca SDI e PEC" del form
            cliente rimanda qui quando e' spento — dire PERCHE' e cosa fare. */}
        <div className="f1-card">
          <div className="f1-card-t">Ricerca automatica codice SDI e PEC</div>
          {companyStato && companyStato.configurato ? (
            <div>
              <span className="f1-pill ok">attiva</span>{' '}
              <span className="f1-muted">
                ambiente <b>{companyStato.ambiente}</b> — nel form cliente il bottone
                «Cerca SDI e PEC» compila codice destinatario e PEC dalla partita IVA.
                Costo indicativo 0,05–0,08 € a ricerca (prime 30 al mese incluse); i clienti
                della Pubblica Amministrazione restano gratuiti via IndicePA.
              </span>
            </div>
          ) : (
            <div>
              <span className="f1-pill">non attiva</span>{' '}
              <span className="f1-muted">
                Il servizio è opzionale: senza, codice destinatario e PEC si inseriscono a mano
                (con <b>0000000</b> la fattura viene comunque recapitata). Per attivarlo:
              </span>
              <ol className="f1-help-ol">
                <li>su <b>console.openapi.com</b> → APIs → <b>Company</b> → attiva il servizio</li>
                <li>sezione <b>OAuth</b> → nuovo token con gli scope <code>GET company.openapi.com/IT-start</code> e <code>GET company.openapi.com/IT-pec</code></li>
                <li>nel file <code>server/.env</code>: <code>FATT_COMPANY_TOKEN=&lt;token&gt;</code> (e <code>FATT_COMPANY_ENV=test</code> per provare senza spendere), poi riavvia il server</li>
              </ol>
            </div>
          )}
        </div>
        <div className="f1-card">
          <div className="f1-card-t">Profilo cedente (sola lettura)</div>
          <div className="f1-prof-row"><b>Denominazione</b><span>{p.denominazione || ((p.nome || '') + ' ' + (p.cognome || '')).trim() || '—'}</span></div>
          <div className="f1-prof-row"><b>Partita IVA</b><span>{p.partitaIva || '—'}</span></div>
          <div className="f1-prof-row"><b>Codice fiscale</b><span>{p.codiceFiscale || '—'}</span></div>
          <div className="f1-prof-row"><b>Regime fiscale</b><span>{p.regimeFiscale || '—'}</span></div>
          <div className="f1-prof-row"><b>Sede</b><span>{[sede.indirizzo, sede.numeroCivico, sede.cap, sede.comune, sede.provincia ? '(' + sede.provincia + ')' : '', sede.nazione].filter(Boolean).join(' ') || '—'}</span></div>
          <div className="f1-prof-row"><b>Contatti</b><span>{p.contatti ? [p.contatti.telefono, p.contatti.email].filter(Boolean).join(' · ') || '—' : '—'}</span></div>
          <div className="f1-prof-row"><b>REA</b><span>{p.iscrizioneRea ? (p.iscrizioneRea.ufficio + ' ' + p.iscrizioneRea.numero) : '—'}</span></div>
        </div>
        <div className="f1-card">
          <div className="f1-card-t">Checklist di partenza</div>
          <div className="f1-check-list">
            1. Completa il <b>profilo azienda</b> (denominazione, P.IVA, regime fiscale, sede completa): è il cedente di ogni fattura.<br />
            2. Registra i <b>clienti</b> con P.IVA/CF e codice destinatario SDI (o PEC).<br />
            3. Prepara le <b>voci</b> ricorrenti (descrizione, prezzo, IVA) per compilare le righe in un click.<br />
            4. Crea la prima <b>bozza</b>, usa <b>Valida</b> per il controllo locale e <b>Anteprima XML</b> per vedere il file FatturaPA.<br />
            5. L&rsquo;<b>invio allo SDI</b> arriva con la fase 2 (sandbox): il numero definitivo si assegna all&rsquo;invio.
          </div>
        </div>
      </React.Fragment>
    );
  }

  // ---- render ----
  const editorAperto = editorId !== undefined;
  return (
    <React.Fragment>
      <div className="f1-h1">Fatturazione</div>
      <div className="f1-sub">Fatture elettroniche (FatturaPA) — bozze, clienti, voci. Invio SDI in fase 2.</div>
      <div className="f1-tabs">
        {TAB_IDS.map((t) => (
          <button key={t} type="button" className={'f1-tab' + ((tab === t && (!editorAperto || t === 'nuova')) ? ' on' : '')}
            onClick={() => goTabRef.current(t)}>
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {editorAperto ? (
        <Editor
          win={win}
          fatturaId={editorId}
          onClose={chiudiEditor}
          toast={toast}
          registerGuard={registerGuard}
        />
      ) : (
        <React.Fragment>
          {tab === 'emesse' && renderEmesse()}
          {tab === 'clienti' && renderClienti()}
          {tab === 'voci' && renderVoci()}
          {tab === 'impostazioni' && renderImpostazioni()}
        </React.Fragment>
      )}

      {cliEditing !== undefined && (
        <ClienteForm
          win={win}
          cliente={cliEditing}
          toast={toast}
          onClose={() => setCliEditing(undefined)}
          onSaved={() => { setCliEditing(undefined); loadClienti(); }}
        />
      )}
      {voceEditing !== undefined && (
        <VoceForm
          win={win}
          voce={voceEditing}
          toast={toast}
          onClose={() => setVoceEditing(undefined)}
          onSaved={() => { setVoceEditing(undefined); loadVoci(); }}
        />
      )}
    </React.Fragment>
  );
}

// ---------------------------------------------------------------------------
// Factory per l'entry del registry (pattern createSopV3): mount IDEMPOTENTE su
// #fatt-root (creato se assente), unmount pulito, navigate con pendingTab se
// il componente React non ha ancora registrato l'handle.
// ---------------------------------------------------------------------------
export function createFattV1(win) {
  let reactRoot = null;
  let mountedEl = null;
  let handle = null;
  let pendingTab = null;

  function ensureRootEl() {
    let r = win.document.getElementById('fatt-root');
    if (!r) {
      r = win.document.createElement('div');
      r.id = 'fatt-root';
      win.document.body.appendChild(r);
    }
    return r;
  }

  function applyTab(itemId) {
    if (TAB_IDS.indexOf(itemId) === -1) return false;
    if (handle && typeof handle.setTab === 'function') return handle.setTab(itemId);
    pendingTab = itemId;
    return true;
  }

  function mount(el) {
    injectStyle(win.document);
    const rootEl = el || ensureRootEl();
    // IDEMPOTENZA (mai perdita dati): il host chiama mount() a OGNI attivazione,
    // anche su modulo GIÀ attivo. Un remount azzererebbe lo stato React e la
    // bozza in modifica sparirebbe SENZA conferma: se il root React è già vivo
    // sullo STESSO elemento ancora in pagina si conserva l'albero e si ricarica
    // SOLO la lista corrente dal server (parità onShow, pattern sop-v3).
    if (reactRoot && mountedEl === rootEl && rootEl && rootEl.isConnected) {
      if (handle && typeof handle.reload === 'function') handle.reload();
      return;
    }
    if (reactRoot) { try { reactRoot.unmount(); } catch (e) { /* già smontato */ } }
    mountedEl = rootEl;
    reactRoot = ReactDOM.createRoot(rootEl);
    reactRoot.render(
      <FattApp
        win={win}
        registerHandle={(h) => {
          handle = h;
          if (h && pendingTab) { const t = pendingTab; pendingTab = null; h.setTab(t); }
        }}
      />
    );
  }

  function unmount() {
    if (reactRoot) {
      try { reactRoot.unmount(); } catch (e) { /* già smontato */ }
      reactRoot = null;
    }
    mountedEl = null;
    handle = null;
    pendingTab = null;
  }

  // Voce del sottomenù/deep-link: voce ignota ⇒ toast dal catalogo (mai silenzio)
  function navigate(itemId) {
    if (applyTab(itemId) !== true) {
      const W = win.Workspaces;
      if (W && W.bus && typeof W.bus.emit === 'function') {
        W.bus.emit('toast', { kind: 'error', msg: label('shell.bridge.missing') + itemId });
      }
    }
  }

  function openDeepLink(l) {
    return !!(l && typeof l.item === 'string' && applyTab(l.item) === true);
  }

  return { mount, unmount, navigate, openDeepLink };
}
